/**
 * Typed, validated frontend configuration (APP-001 §20, §33 invariant 20).
 *
 * All `VITE_*` values are read here, validated once at startup, and exposed as
 * a frozen, typed object. Missing required variables, secret Clerk keys,
 * non-HTTPS production origins, localhost in production, and wildcard origins
 * are all rejected with an explicit error before the app renders.
 *
 * SECURITY INVARIANTS enforced here:
 *  - Clerk *secret* keys (sk_/sk_test_/sk_live_) never reach the browser.
 *  - Production API/app URLs MUST be https and first-party.
 *  - localhost is only valid in development/staging.
 *  - Wildcard hosts ('*') are never allowed as app/API origins.
 *  - No Stripe/Clerk/db secrets are declared on the frontend env surface.
 */

const APP_BASE_URL = 'https://app.signuprisk.com';

/** Canonical first-party production origins (APP-001 §1). */
export const FIRST_PARTY_ORIGINS = {
  app: 'app.signuprisk.com',
  api: 'api.signuprisk.com',
  marketing: 'signuprisk.com',
  docs: 'docs.signuprisk.com',
} as const;

export type Environment = 'development' | 'staging' | 'production';

class EnvValidationError extends Error {
  constructor(public readonly field: string, message: string) {
    super(`[env] ${field}: ${message}`);
    this.name = 'EnvValidationError';
  }
}

/**
 * Runtime-injected configuration (container deployments). /env.js is a tiny
 * same-origin script served no-cache and loaded before the bundle; the
 * container entrypoint writes it at startup from deploy-time VITE_* values
 * (see docker-entrypoint.sh). Values present there take precedence over the
 * build-time Vite defines so a single image can be promoted across
 * environments. Local dev, unit tests, and the e2e harness never inject it
 * and keep using build-time values only.
 */
declare global {
  interface Window {
    __APP_CONFIG__?: Partial<Record<string, string>>;
  }
}

function readEnv(key: string): string {
  const injected = typeof window !== 'undefined' ? window.__APP_CONFIG__?.[key] : undefined;
  if (typeof injected === 'string' && injected.trim() !== '') return injected.trim();
  // import.meta.env is statically replaced at build time by Vite.
  const value = import.meta.env[key];
  return typeof value === 'string' ? value.trim() : '';
}

function parseOrigin(raw: string, field: string): URL {
  if (!raw) {
    throw new EnvValidationError(field, 'is required');
  }
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    throw new EnvValidationError(field, `is not a valid URL (got "${redact(raw)}")`);
  }
  if (!url.protocol.startsWith('http')) {
    throw new EnvValidationError(field, `must use http(s) protocol (got "${url.protocol}")`);
  }
  if (url.username || url.password) {
    throw new EnvValidationError(field, 'must not carry credentials');
  }
  return url;
}

/** Reject Clerk secret keys; only publishable keys are safe for the browser. */
function validateClerkPublishableKey(raw: string): string {
  if (!raw) {
    throw new EnvValidationError('VITE_CLERK_PUBLISHABLE_KEY', 'is required');
  }
  const lower = raw.toLowerCase();
  // Clerk keys: publishable pk_test_/pk_live_; secret sk_test_/sk_live_.
  if (lower.startsWith('sk_')) {
    throw new EnvValidationError(
      'VITE_CLERK_PUBLISHABLE_KEY',
      'a Clerk SECRET key was supplied — publishable (pk_) keys only; secrets must never reach the browser',
    );
  }
  if (!lower.startsWith('pk_test_') && !lower.startsWith('pk_live_')) {
    throw new EnvValidationError(
      'VITE_CLERK_PUBLISHABLE_KEY',
      'must be a Clerk publishable key (pk_test_… or pk_live_…)',
    );
  }
  return raw;
}

function assertProductionHttps(url: URL, field: string, env: Environment): void {
  if (env !== 'production') return;
  if (url.protocol !== 'https:') {
    throw new EnvValidationError(field, 'must use https in production');
  }
  if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
    throw new EnvValidationError(field, 'must not point at localhost in production');
  }
}

function assertNoWildcard(url: URL, field: string): void {
  if (url.hostname.startsWith('*')) {
    throw new EnvValidationError(field, 'wildcard host is not allowed');
  }
}

/** Redact values in error messages; env values are not logged wholesale. */
function redact(value: string): string {
  if (!value) return '<empty>';
  if (value.length <= 6) return '***';
  return `${value.slice(0, 3)}…${value.slice(-2)}`;
}

export interface AppConfig {
  /** Clerk publishable key (browser-safe). */
  clerkPublishableKey: string;
  /** Go API base URL (e.g. https://api.signuprisk.com). */
  apiBaseUrl: string;
  /** Canonical dashboard origin (this app). */
  appBaseUrl: string;
  /** Marketing site origin (Webflow). */
  marketingBaseUrl: string;
  /** Public docs origin. */
  docsBaseUrl: string;
  /** Deployment environment. */
  environment: Environment;
  /** True when running against production. */
  isProduction: boolean;
  /** True in local/preview development. */
  isDevelopment: boolean;
}

function buildConfig(): AppConfig {
  const environment = parseEnvironment(readEnv('VITE_ENVIRONMENT'));

  const clerkPublishableKey = validateClerkPublishableKey(readEnv('VITE_CLERK_PUBLISHABLE_KEY'));

  const apiUrl = parseOrigin(readEnv('VITE_API_BASE_URL'), 'VITE_API_BASE_URL');
  assertNoWildcard(apiUrl, 'VITE_API_BASE_URL');
  assertProductionHttps(apiUrl, 'VITE_API_BASE_URL', environment);

  const appUrl = parseOrigin(readEnv('VITE_APP_BASE_URL'), 'VITE_APP_BASE_URL');
  assertNoWildcard(appUrl, 'VITE_APP_BASE_URL');
  assertProductionHttps(appUrl, 'VITE_APP_BASE_URL', environment);

  const marketingUrl = parseOrigin(
    readEnv('VITE_MARKETING_BASE_URL') || 'https://signuprisk.com',
    'VITE_MARKETING_BASE_URL',
  );
  const docsUrl = parseOrigin(
    readEnv('VITE_DOCS_BASE_URL') || 'https://docs.signuprisk.com',
    'VITE_DOCS_BASE_URL',
  );

  return Object.freeze({
    clerkPublishableKey,
    apiBaseUrl: apiUrl.origin,
    appBaseUrl: appUrl.origin,
    marketingBaseUrl: marketingUrl.origin,
    docsBaseUrl: docsUrl.origin,
    environment,
    isProduction: environment === 'production',
    isDevelopment: environment === 'development',
  });
}

function parseEnvironment(raw: string): Environment {
  if (!raw) return 'development';
  if (raw === 'development' || raw === 'staging' || raw === 'production') return raw;
  throw new EnvValidationError(
    'VITE_ENVIRONMENT',
    `must be development|staging|production (got "${raw}")`,
  );
}

let cached: AppConfig | null = null;
let cachedError: Error | null = null;

/**
 * Resolve and validate configuration. The first call performs validation and
 * caches the result; subsequent calls return the cached value. On failure the
 * error is cached so callers (including the error boundary) can render it.
 */
export function loadConfig(): AppConfig {
  if (cached) return cached;
  if (cachedError) throw cachedError;
  try {
    cached = buildConfig();
    return cached;
  } catch (err) {
    cachedError = err instanceof Error ? err : new Error(String(err));
    throw cachedError;
  }
}

/**
 * Reset the cache. Tests use this to inject alternate configurations.
 */
export function __resetConfigForTests(): void {
  cached = null;
  cachedError = null;
}

export { APP_BASE_URL };
